/**
 * Admin role/capability hooks for screen-level access checks.
 *
 * Role and capability definitions and the pure resolver live in
 * `@repo/shared/roles-capabilities`; this file adds the React hooks that
 * resolve the current session's roles. `useAccessCheck` distinguishes
 * unauthenticated (`unauthorized`) from authenticated-but-lacking
 * (`forbidden`) so the routing layer can render them differently.
 */

import { ROLES, roleHasCapability } from '@repo/shared/roles-capabilities';
import { useSiloMember } from 'component/silo-member-context';

import { usePersonaKey } from './datastore';

export function useMyRoles(): string[] {
    const member = useSiloMember();
    return member?.roles ?? [];
}

export function useIsAdmin() {
    const myRoles = useMyRoles();
    return myRoles && myRoles.length > 0;
}

export function useHasCapability(capability?: string) {
    const myRoles = useMyRoles();
    return roleHasCapability(myRoles, capability);
}

export function getRoles() {
    return ROLES;
}

export function useAccessCheck(capability?: string) {
    const personaKey = usePersonaKey();
    const hasCapability = useHasCapability(capability);
    const isAdmin = useIsAdmin();

    if (personaKey === null) return { status: 'unauthorized' };
    if (!capability && !isAdmin) return { status: 'forbidden' };
    if (capability && !hasCapability) return { status: 'forbidden' };
    return { status: 'allowed' };
}
